import { HttpParams } from '@angular/common/http';
import { forwardRef, Inject, Injectable } from '@angular/core';
import * as moment_tz from 'moment-timezone';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { TimezoneSelectorComponent } from '../dashboard-area/timezone-selector/timezone-selector.component';
import { Organization, Partner } from '../model';
import { Customer } from "../model/customer";
import { Location } from "../model/location";
import { Tag } from '../model/tag';
import { AbstractContextService } from '../shared/class/abstract-context-service.class';
import { TagService } from '../shared/tags/tag.service';
import { AuthenticationService } from './authentication.service';
import { ThingContextService } from './thing-context.service';

@Injectable()
export class ContextService extends AbstractContextService {

    private currentCustomer: Customer;

    private currentLocation: Location;

    private currentPartner: Partner;

    private currentOrganization: Organization;

    private tags: Tag[] = [];

    private contextCustomerTags: { [customerId: string]: Tag[] } = {};

    private contextLocationTags: { [locationId: string]: Tag[] } = {};

    private contextPartnerTags: { [partnerId: string]: Tag[] } = {};

    private tagsSubject = new BehaviorSubject<Tag[]>([]);

    private preferredTimezone: string;

    private contextTimezoneChangedSubject$: Subject<void> = new Subject();

    static DEFAULT_TIMEZONE = 'UTC';

    constructor(
        @Inject(forwardRef(() => AuthenticationService)) private authenticationService: AuthenticationService,
        @Inject(forwardRef(() => TagService)) private tagService: TagService,
        @Inject(forwardRef(() => ThingContextService)) private thingContextService: ThingContextService,
    ) { super() }

    setCurrentCustomer(customer: Customer) {
        this.currentCustomer = customer;
        if (customer) {
            this.setCustomerTags(customer.id);
        }
        this.updateContextTimezoneChangedSubject();
    }

    getCurrentCustomer(): Customer {
        return this.currentCustomer;
    }

    setCurrentLocation(location: Location) {
        this.currentLocation = location;
        if (location) {
            this.setLocationTags(location.id);
        }
        this.updateContextTimezoneChangedSubject();
    }

    getCurrentLocation(): Location {
        return this.currentLocation;
    }

    setCurrentPartner(partner: Partner) {
        this.currentPartner = partner;
        if (partner) {
            this.setPartnerTags(partner.id);
        }
    }

    getCurrentPartner(): Partner {
        return this.currentPartner;
    }

    setCurrentOrganization(organization: Organization) {
        this.currentOrganization = organization;
    }

    getCurrentOrganization(): Organization {
        return this.currentOrganization;
    }

    static getCustomerFromLocation(location: Location): Customer {
        if (location.customer) {
            return location.customer;
        } else {
            return null;
        }
    }

    initTags(): Promise<any> {
        return this.tagService.getTags().then(tags => {
            this.tags = tags;
            this.tagsSubject.next(tags);
        }).catch(() => { })
    }

    getTags(): string[] {
        return this.getTagObjects()?.map(t => { return t.name });
    }

    getTagObjects(): Tag[] {
        return this.tags;
    }

    setCustomerTags(customerId: string): void {
        if (!this.contextCustomerTags[customerId]) {
            const params = new HttpParams().set('customerId', customerId);
            this.tagService.getTags(params)
                .then(tags => this.contextCustomerTags[customerId] = tags)
                .catch(() => { });
        }
    }

    getCustomerTagObjects(): Tag[] {
        if (!this.currentCustomer) {
            if (this.authenticationService.isCustomerUser()) {
                return this.getTagObjects();
            }
        } else {
            if (this.contextCustomerTags[this.currentCustomer.id]) {
                return this.contextCustomerTags[this.currentCustomer.id];
            }
        }
        return [];
    }

    getCustomerTags(): string[] {
        return this.getCustomerTagObjects()?.map(t => { return t.name });
    }

    setLocationTags(locationId: string): void {
        if (!this.contextLocationTags[locationId]) {
            const params = new HttpParams().set('locationId', locationId);
            this.tagService.getTags(params)
                .then(tags => this.contextLocationTags[locationId] = tags)
                .catch(() => { });
        }
    }

    getLocationTagObjects(): Tag[] {
        if (!this.currentLocation) {
            if (this.authenticationService.isLocationUser()) {
                return this.getTagObjects();
            }
        } else {
            if (this.contextLocationTags[this.currentLocation.id]) {
                return this.contextLocationTags[this.currentLocation.id];
            }
        }
        return [];
    }

    getLocationTags(): string[] {
        return this.getLocationTagObjects()?.map(t => { return t.name })
    }

    setPartnerTags(partnerId: string): void {
        if (!this.contextPartnerTags[partnerId]) {
            const params = new HttpParams().set('partnerId', partnerId);
            this.tagService.getTags(params)
                .then(tags => this.contextPartnerTags[partnerId] = tags)
                .catch(() => { });
        }
    }

    getPartnerTagObjects(): Tag[] {
        if (!this.currentPartner) {
            if (this.authenticationService.isPartnerUser()) {
                return this.getTagObjects();
            }
        } else {
            if (this.contextPartnerTags[this.currentPartner.id]) {
                return this.contextPartnerTags[this.currentPartner.id];
            }
        }
        return [];
    }

    getPartnerTags(): string[] {
        return this.getPartnerTagObjects()?.map(t => { return t.name })
    }

    getTagsSubject(): Observable<Tag[]> {
        return this.tagsSubject.asObservable();
    }

    getTimezone(): string {
        const tenant = this.authenticationService.getTenant();
        const user = this.authenticationService.getUser();
        if (user?.contextAwareAutomaticTimezoneEnabled) {
            return this.getContextTimezone() || user?.timezone || ContextService.DEFAULT_TIMEZONE;
        } else {
            const preferredTimezone = tenant.timezoneSelectorEnabled ? this.preferredTimezone : null;
            return preferredTimezone || user?.timezone || ContextService.DEFAULT_TIMEZONE;
        }
    }

    getContextTimezone(): string {
        return this.thingContextService?.getCurrentThing()?.timezone || this.getCurrentLocation()?.timezone || this.getCurrentCustomer()?.timezone;
    }

    setPreferredTimezone(value: string): void {
        this.preferredTimezone = value;
    }

    private updateContextTimezoneChangedSubject() {
        this.contextTimezoneChangedSubject$.next();
    }

    getContextTimezoneChangedSubject(): Observable<void> {
        return this.contextTimezoneChangedSubject$.asObservable();
    }

    getTimezoneOffset(timezone: string): number {
        return moment_tz.tz(timezone).utcOffset();
    }

    isTimezoneSelectorVisible(): boolean {
        const contextTimezone = this.getContextTimezone();
        const userTimezone = this.authenticationService.getUser()?.timezone || 'UTC';
        const isTimezoneConflict = contextTimezone && (this.getTimezoneOffset(userTimezone) != this.getTimezoneOffset(contextTimezone));
        const isTimezoneSelectorPinned = localStorage.getItem(TimezoneSelectorComponent.LOCAL_STORAGE_TIMEZONE_SELECTOR_STATUS_KEY) == 'pinned';
        return isTimezoneSelectorPinned || isTimezoneConflict || !!this.preferredTimezone;
    }
}